import PropTypes from "prop-types"
import React, { Component } from "react"
import { connect } from "react-redux"
import { Link } from "react-router-dom"
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap"
import {
  ChevronDown,
  LogOut,
  Maximize2,
  Menu,
  Minimize2,
  Search,
} from "lucide-react"

import logo from "../../assets/images/VeeperLogo.svg"

// Matches the `max-width: 992px` breakpoint where the sidebar becomes a drawer
const MOBILE_BREAKPOINT = 992
const IS_MAC =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad/i.test(navigator.platform || "")

const capitalize = value => value.charAt(0).toUpperCase() + value.slice(1)

/**
 * Signed-in user from localStorage. Login only stores `email`, so the display
 * name falls back to the email's local part.
 */
const getCurrentUser = () => {
  try {
    const user = JSON.parse(localStorage.getItem("authUser") || "{}") || {}
    const email = user.email || ""
    const name =
      user.displayName ||
      user.username ||
      (email ? capitalize(email.split("@")[0]) : "Admin")
    return { name, email }
  } catch (e) {
    return { name: "Admin", email: "" }
  }
}

const getInitials = name =>
  name
    .split(/[\s._-]+/)
    .filter(Boolean)
    .map(part => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "A"

const getFullscreenElement = () =>
  document.fullscreenElement || document.webkitFullscreenElement
const FULLSCREEN_SUPPORTED =
  typeof document !== "undefined" &&
  Boolean(document.fullscreenEnabled || document.webkitFullscreenEnabled)

class Header extends Component {
  constructor(props) {
    super(props)
    this.state = {
      user: getCurrentUser(),
      userMenuOpen: false,
      isFullscreen: false,
    }
  }

  componentDidMount() {
    document.addEventListener("fullscreenchange", this.syncFullscreen)
    document.addEventListener("webkitfullscreenchange", this.syncFullscreen)
  }

  componentWillUnmount() {
    document.removeEventListener("fullscreenchange", this.syncFullscreen)
    document.removeEventListener("webkitfullscreenchange", this.syncFullscreen)
  }

  syncFullscreen = () =>
    this.setState({ isFullscreen: Boolean(getFullscreenElement()) })

  toggleFullscreen = () => {
    const root = document.documentElement
    if (getFullscreenElement()) {
      const exit = document.exitFullscreen || document.webkitExitFullscreen
      if (exit) exit.call(document)
    } else {
      const request = root.requestFullscreen || root.webkitRequestFullscreen
      if (request) request.call(root)
    }
  }

  toggleUserMenu = () =>
    this.setState(state => ({ userMenuOpen: !state.userMenuOpen }))

  // Drawer open on mobile ("condensed"), full sidebar on desktop ("default")
  isMenuExpanded() {
    const isMobile = window.innerWidth <= MOBILE_BREAKPOINT
    return isMobile
      ? this.props.leftSideBarType === "condensed"
      : this.props.leftSideBarType !== "condensed"
  }

  renderBrand() {
    // Dark/light variants are switched by the sidebar theme in _topbar.scss
    return ["logo-dark", "logo-light"].map(variant => (
      <Link
        key={variant}
        to="/dashboard"
        className={`logo ${variant}`}
        aria-label="Veeper Admin Panel home"
      >
        <span className="logo-sm">
          <span className="brand-icon-box">
            <img src={logo} alt="" height="18" />
          </span>
        </span>
        <span className="logo-lg">
          <span className="brand-icon-box">
            <img src={logo} alt="" height="18" />
          </span>
          <span className="brand-text-box">
            <span className="brand-name">Veeper</span>
            <span className="brand-subtitle">Admin Panel</span>
          </span>
        </span>
      </Link>
    ))
  }

  renderUserMenu() {
    const { user, userMenuOpen } = this.state
    const initials = getInitials(user.name)

    return (
      <Dropdown
        isOpen={userMenuOpen}
        toggle={this.toggleUserMenu}
        className="header-user"
      >
        <DropdownToggle
          tag="button"
          type="button"
          className="header-user__toggle"
          aria-label={`Account menu for ${user.name}`}
        >
          <span className="header-user__avatar" aria-hidden="true">
            {initials}
          </span>
          <span className="header-user__name">{user.name}</span>
          <ChevronDown
            className="header-user__chevron"
            size={16}
            strokeWidth={2.2}
            aria-hidden="true"
          />
        </DropdownToggle>
        <DropdownMenu end className="header-user__menu">
          <div className="header-user__summary">
            <span
              className="header-user__avatar header-user__avatar--lg"
              aria-hidden="true"
            >
              {initials}
            </span>
            <div className="header-user__identity">
              <span className="header-user__summary-name">{user.name}</span>
              {user.email && (
                <span className="header-user__email" title={user.email}>
                  {user.email}
                </span>
              )}
              <span className="header-user__role">Administrator</span>
            </div>
          </div>
          <DropdownItem divider />
          <DropdownItem
            tag={Link}
            to="/logout"
            className="header-user__item header-user__item--danger"
          >
            <LogOut size={16} strokeWidth={2} aria-hidden="true" />
            Sign out
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    )
  }

  render() {
    const { isFullscreen } = this.state
    const { onOpenSearch } = this.props
    const shortcut = IS_MAC ? "⌘K" : "Ctrl K"

    return (
      <header id="page-topbar" className="veeper-topbar">
        <div className="navbar-header">
          <div className="header-left">
            <div className="navbar-brand-box">{this.renderBrand()}</div>

            <button
              type="button"
              onClick={this.props.toggleMenuCallback}
              className="header-icon-btn header-toggle-btn"
              id="vertical-menu-btn"
              aria-label="Toggle navigation menu"
              aria-controls="app-sidebar"
              aria-expanded={this.isMenuExpanded()}
            >
              <Menu size={20} strokeWidth={2} aria-hidden="true" />
            </button>

            <button
              type="button"
              className="header-search"
              onClick={onOpenSearch}
              aria-label="Search pages"
              aria-keyshortcuts="Control+K Meta+K"
              aria-haspopup="dialog"
            >
              <Search size={16} strokeWidth={2} aria-hidden="true" />
              <span className="header-search__placeholder">Search pages…</span>
              <kbd className="header-search__kbd" aria-hidden="true">
                {shortcut}
              </kbd>
            </button>
          </div>

          <div className="header-right">
            <button
              type="button"
              className="header-icon-btn header-search-icon"
              onClick={onOpenSearch}
              aria-label="Search pages"
              aria-haspopup="dialog"
            >
              <Search size={18} strokeWidth={2} aria-hidden="true" />
            </button>

            {FULLSCREEN_SUPPORTED && (
              <button
                type="button"
                className="header-icon-btn header-fullscreen-btn"
                onClick={this.toggleFullscreen}
                aria-pressed={isFullscreen}
                aria-label={
                  isFullscreen ? "Exit fullscreen" : "Enter fullscreen"
                }
                title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
              >
                {isFullscreen ? (
                  <Minimize2 size={18} strokeWidth={2} aria-hidden="true" />
                ) : (
                  <Maximize2 size={18} strokeWidth={2} aria-hidden="true" />
                )}
              </button>
            )}

            <span className="header-separator" aria-hidden="true" />

            {this.renderUserMenu()}
          </div>
        </div>
      </header>
    )
  }
}

const mapStatetoProps = state => {
  const { layoutType, leftSideBarType } = state.Layout
  return { layoutType, leftSideBarType }
}

export default connect(mapStatetoProps)(Header)

Header.propTypes = {
  leftSideBarType: PropTypes.string,
  toggleMenuCallback: PropTypes.func,
  onOpenSearch: PropTypes.func,
}
